import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FormError, SubmitButton } from "@/components/common";
import { api, errMsg, errDetail } from "@/lib/api";
import { AU_STATES } from "@/lib/format";
import { t } from "@/i18n";

export const EMPTY_VEHICLE = { make: "", model: "", year: "", plate: "", state: "WA", odometer: "", odometer_date: "", vin: "" };

export const vehiclePayload = (v) => ({
  make: v.make, model: v.model, plate: v.plate, state: v.state, vin: v.vin || null,
  year: v.year === "" || v.year == null ? null : Number(v.year),
  odometer: v.odometer === "" || v.odometer == null ? null : Number(v.odometer),
  odometer_date: v.odometer_date || null,
});

export function VehicleFields({ v, setV, onPlateBlur }) {
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="vehicle-fields">
      <Field label={t("vehicles.make")} htmlFor="vf-make"><Input id="vf-make" required value={v.make} onChange={set("make")} data-testid="vehicle-make-input" /></Field>
      <Field label={t("vehicles.model")} htmlFor="vf-model"><Input id="vf-model" required value={v.model} onChange={set("model")} data-testid="vehicle-model-input" /></Field>
      <Field label={t("vehicles.year")} htmlFor="vf-year"><Input id="vf-year" type="number" min={1900} max={2100} value={v.year ?? ""} onChange={set("year")} data-testid="vehicle-year-input" /></Field>
      <div className="grid grid-cols-[1fr_110px] gap-3">
        <Field label={t("vehicles.plate")} htmlFor="vf-plate"><Input id="vf-plate" required value={v.plate} onChange={(e) => setV({ ...v, plate: e.target.value.toUpperCase() })} onBlur={onPlateBlur} className="font-mono-plate" data-testid="vehicle-plate-input" /></Field>
        <Field label={t("vehicles.state")}>
          <Select value={v.state} onValueChange={(s) => setV({ ...v, state: s })}>
            <SelectTrigger data-testid="vehicle-state-select"><SelectValue /></SelectTrigger>
            <SelectContent>{AU_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label={t("vehicles.odometer")} htmlFor="vf-odo"><Input id="vf-odo" type="number" min={0} value={v.odometer ?? ""} onChange={set("odometer")} data-testid="vehicle-odometer-input" /></Field>
      <Field label={t("vehicles.odometerDate")} htmlFor="vf-odod"><Input id="vf-odod" type="date" value={v.odometer_date ?? ""} onChange={set("odometer_date")} data-testid="vehicle-odometer-date-input" /></Field>
      <Field label={t("vehicles.vin")} htmlFor="vf-vin" hint={t("vehicles.vinHint")} className="sm:col-span-2"><Input id="vf-vin" maxLength={17} value={v.vin ?? ""} onChange={(e) => setV({ ...v, vin: e.target.value.toUpperCase() })} className="font-mono-plate" data-testid="vehicle-vin-input" /></Field>
    </div>
  );
}

export function VehicleForm({ vehicle, basePath, customers, customerId, onSaved, onDuplicate }) {
  const [v, setV] = useState(vehicle ? { ...EMPTY_VEHICLE, ...vehicle } : EMPTY_VEHICLE);
  const [cid, setCid] = useState(vehicle?.customer_id || customerId || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (customers && !cid) return setError(t("vehicles.chooseOwner"));
    setBusy(true);
    setError("");
    const payload = { ...vehiclePayload(v), ...(basePath === "/vehicles" ? { customer_id: cid } : {}) };
    try {
      const { data } = vehicle ? await api.put(`${basePath}/${vehicle.id}`, payload) : await api.post(basePath, payload);
      onSaved(data);
    } catch (err) {
      const d = errDetail(err);
      if (d?.matches) onDuplicate?.(d.matches);
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="vehicle-form">
      <FormError message={error} />
      {customers && (
        <Field label={t("vehicles.owner")}>
          <Select value={cid} onValueChange={setCid}>
            <SelectTrigger data-testid="vehicle-owner-select"><SelectValue placeholder={t("vehicles.chooseOwner")} /></SelectTrigger>
            <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{`${c.first_name} ${c.last_name}`.trim()}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      )}
      <VehicleFields v={v} setV={setV} />
      <div className="gf-modal-footer">
      <SubmitButton busy={busy} className="w-full" data-testid="vehicle-save-button">{vehicle ? t("common.save") : t("vehicles.add")}</SubmitButton>
      </div>
    </form>
  );
}
